/**
 * The Subjects home (design v2 screen 4): a ruled list, each row the subject's name, its
 * qualification and format, a chapter strip for its ready book and a one-line summary; then
 * "Add a subject" on the sunk ground, and the plan's subject count by the title. With no
 * subjects yet it is the first run (screen 9): the empty desk on --room, one form, three steps.
 */
import { useEffect, useState } from "react";
import type {
  Api,
  Caps,
  ChapterWire,
  Doc,
  Meter,
  PaperRow,
  PlanKind,
  Profile,
  Subject,
} from "../api/api";
import { brand } from "../brand";
import type { ApiFailure } from "../api/client";
import { LoadError } from "../states/LoadError";
import { SkeletonRows } from "../states/Skeleton";
import { studiedChapters } from "../study/studied";
import { strip, summarise, type Summary } from "./summary";
import "../styles/library.css";
import { apiMessage } from "../text/errors";

const WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
const count = (n: number) => WORDS[n] ?? n.toLocaleString("en-GB");

/** The allowance under the first-run desk, from the caps billing sends. */
export function allowanceNote(kind: PlanKind | undefined, caps: Caps | undefined): string | null {
  if (!caps || caps.pages <= 0) return null;
  const papers = `${count(caps.papers)} practice paper${caps.papers === 1 ? "" : "s"}`;
  if (kind === "trial") {
    return `Your trial covers one book of up to ${caps.pages.toLocaleString("en-GB")} pages and ${papers}.`;
  }
  return (
    `Your plan covers ${caps.pages.toLocaleString("en-GB")} pages of textbook and ` +
    `${caps.papers.toLocaleString("en-GB")} practice paper${caps.papers === 1 ? "" : "s"} a month.`
  );
}

type Detail = { summary: Summary; cells: string[]; board: string | null };

async function detail(api: Api, s: Subject): Promise<Detail> {
  const [d, p, f] = await Promise.all([
    api.listDocuments(s.id),
    api.listPapers(s.id),
    api.getProfile(s.id),
  ]);
  const docs: Doc[] = d.ok ? d.data.documents : [];
  const papers: PaperRow[] = p.ok ? p.data.papers : [];
  const profile: Profile | null = f.ok ? f.data : null;
  const book = docs.find((x) => x.kind === "textbook" && x.book_status === "ready");
  let chapters: ChapterWire[] = [];
  let studied: number[] = [];
  if (book?.book_id) {
    const c = await api.listChapters(book.book_id);
    chapters = c.ok ? c.data.chapters : [];
    studied = studiedChapters(book.book_id);
  }
  const done = chapters.filter((c) => studied.includes(c.number)).length;
  return {
    summary: summarise({ docs, papers, chapters, studied: done }),
    cells: strip(chapters, studied),
    board: profile?.status === "confirmed" && profile.board ? profile.board : null,
  };
}

export function SubjectsScreen({
  api,
  onOpen,
  onNotEntitled,
}: {
  api: Api;
  onOpen: (id: string) => void;
  /** A 402 on "Add subject": the Shell sends the student to the chooser (spec §9.5). */
  onNotEntitled?: () => void;
}) {
  const [subjects, setSubjects] = useState<Subject[] | null>(null);
  const [failed, setFailed] = useState<ApiFailure | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [details, setDetails] = useState<Record<string, Detail>>({});
  const [meter, setMeter] = useState<Meter | null>(null);
  const [plan, setPlan] = useState<{ kind?: PlanKind; caps?: Caps } | null>(null);
  const [name, setName] = useState("");
  const [qualification, setQualification] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loadMeter = () =>
    void api.billingMe().then((r) => {
      if (!r.ok) return;
      setMeter(r.data.meters.find((m) => m.name === "subjects") ?? null);
      setPlan({ kind: r.data.kind, caps: r.data.caps });
    });

  useEffect(() => {
    let live = true;
    void api.listSubjects().then((r) => {
      if (!live) return;
      // A failed list is not an empty desk: say so and offer Try again.
      if (!r.ok) return setFailed(r);
      const list = r.data.subjects;
      setFailed(null);
      setSubjects(list);
      for (const s of list)
        void detail(api, s).then((d) => live && setDetails((x) => ({ ...x, [s.id]: d })));
    });
    loadMeter();
    return () => {
      live = false;
    };
  }, [api, attempt]);

  const addForm = (className: string, title: string) => (
    <form
      className={className}
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        const r = await api.createSubject(name, qualification);
        if (!r.ok) {
          if (r.status === 402) return onNotEntitled?.();
          return setError(
            r.error === "subjects_limit"
              ? "Your plan's subjects are all in use. Choose a bigger plan to add another."
              : apiMessage(r.error),
          );
        }
        setSubjects([...(subjects ?? []), r.data]);
        setDetails((x) => ({
          ...x,
          [r.data.id]: {
            summary: summarise({ docs: [], papers: [], chapters: [], studied: 0 }),
            cells: [],
            board: null,
          },
        }));
        setName("");
        setQualification("");
        loadMeter();
      }}
    >
      <h2 className="add-subject__title">{title}</h2>
      <div className="add-subject__fields">
        <label className="add-subject__name">
          Name <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label>
          Qualification{" "}
          <input
            value={qualification}
            onChange={(e) => setQualification(e.target.value)}
            placeholder="e.g. GCSE, A-level, national exams"
            required
          />
        </label>
        <button type="submit" className="button button--primary">
          Add subject
        </button>
      </div>
      {error && (
        <p role="alert" className="field-error">
          {error}
        </p>
      )}
    </form>
  );

  if (subjects !== null && subjects.length === 0) {
    const note = allowanceNote(plan?.kind, plan?.caps);
    return (
      <main className="first-run">
        <div className="first-run__inner">
          <p className="eyebrow first-run__eyebrow">Your desk is empty</p>
          <h1 className="first-run__title">
            Start with the book <em>you already study from.</em>
          </h1>
          <p className="first-run__lede">
            Add a subject, upload its textbook, and check the chapters {brand.name} finds. Your
            first flashcards are usually ready about ten minutes later.
          </p>
          <div className="first-run__sheet">
            {addForm("add-subject add-subject--first", "First subject")}
            <ol className="first-run__steps" aria-label="What happens next">
              <li>
                <span className="first-run__n">1</span>
                <strong>Upload the textbook</strong>
                <span>A PDF, scanned is fine. Free until step 2.</span>
              </li>
              <li>
                <span className="first-run__n">2</span>
                <strong>Check the chapters</strong>
                <span>You see what it costs before you confirm.</span>
              </li>
              <li>
                <span className="first-run__n">3</span>
                <strong>Study tonight</strong>
                <span>Cards on your phone. Papers when you have a past paper.</span>
              </li>
            </ol>
          </div>
          {note && <p className="first-run__note">{note}</p>}
        </div>
      </main>
    );
  }

  return (
    <main className="library">
      <header className="library__head">
        <h1 className="library__title">Subjects</h1>
        {meter && meter.limit > 0 && (
          <p className="library__limit num">
            {/* The list is the count; billing's is stored only when a subject is created, so
                a workspace with subjects from before its meter existed reads 0 there. */}
            {subjects?.length ?? meter.used} of {meter.limit} subjects on your plan
          </p>
        )}
      </header>
      {failed ? (
        <LoadError failure={failed} onRetry={() => setAttempt((n) => n + 1)} />
      ) : subjects === null ? (
        <SkeletonRows />
      ) : (
        <ul className="subjects">
          {subjects.map((s) => {
            const d = details[s.id];
            return (
              <li key={s.id}>
                <button className="subject-row" onClick={() => onOpen(s.id)}>
                  <span className="subject-row__body">
                    <span className="subject-row__name">
                      <span className="subject-row__title">{s.name}</span>
                      <span className="subject-row__qual">
                        {s.qualification}
                        {d?.board ? ` · ${d.board} format` : ""}
                      </span>
                    </span>
                    {d && d.cells.length > 0 && (
                      <span className="chapter-strip" aria-hidden="true">
                        {d.cells.map((c, i) => (
                          <span
                            key={i}
                            className={`chapter-strip__cell chapter-strip__cell--${c}`}
                          />
                        ))}
                      </span>
                    )}
                    {d?.summary.pill && (
                      <span className={`pill pill--${d.summary.pill.kind}`}>
                        {d.summary.pill.label}
                      </span>
                    )}
                    <span className="subject-row__line">{d ? d.summary.line : " "}</span>
                  </span>
                  <span className="subject-row__go" aria-hidden="true">
                    ›
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {subjects !== null && addForm("add-subject", "Add a subject")}
    </main>
  );
}
